import {
  Link as RouterLink,
  useNavigate,
  useParams,
  useLocation,
} from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Container,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useAuth } from '@/common/auth/hooks/useAuth';
import { BlueprintUploadForm } from '@/common/components/blueprints/BlueprintUploadForm';
import FadeContent from '@/common/components/reactBits/FadeContent';
import { useModeTokens } from '@/common/components/ui/useModeTokens';
import {
  useBlueprintCommunityStatusQuery,
  useBlueprintQuery,
  useBlueprintTagsQuery,
} from '@/generated/graphql';

export default function BlueprintUploadPage() {
  const t = useModeTokens();
  const { blueprintId } = useParams();
  const navigate = useNavigate();
  const newVersion = useLocation().pathname.endsWith('/versions/new');
  const { userName } = useAuth();
  const community = useBlueprintCommunityStatusQuery({
    fetchPolicy: 'network-only',
  });
  const detail = useBlueprintQuery({
    variables: { id: blueprintId || '' },
    skip: !blueprintId,
    fetchPolicy: 'network-only',
  });
  const tags = useBlueprintTagsQuery({
    variables: { search: '' },
    fetchPolicy: 'cache-and-network',
  });
  const edit = blueprintId ? detail.data?.blueprint : undefined;
  const returnPath = blueprintId
    ? `/blueprints/${encodeURIComponent(blueprintId)}`
    : '/blueprints';
  const loading = community.loading || (!!blueprintId && detail.loading);
  const error = community.error || (blueprintId && detail.error);
  const blocked = community.data?.blueprintCommunityStatus.blocked;
  const unavailable =
    !!blueprintId && (!edit?.isOwner || edit.moderationState !== 'active');

  return (
    <Container
      maxWidth={false}
      sx={{ maxWidth: 1240, px: { xs: 0.5, sm: 2 }, pb: 5 }}
    >
      <Button
        component={RouterLink}
        to={returnPath}
        startIcon={<ArrowBackIcon />}
        size="small"
        color="inherit"
        sx={{
          mb: { xs: 2.5, sm: 3 },
          ml: -1,
          color: 'text.secondary',
          minHeight: 40,
        }}
      >
        {blueprintId ? 'Back to blueprint' : 'Back to Blueprints'}
      </Button>
      <FadeContent playOnMount duration={450}>
        <Box
          component="header"
          sx={{
            pb: { xs: 3, sm: 4 },
            mb: { xs: 3, sm: 4 },
            borderBottom: `1px solid ${t.borders.hairline}`,
          }}
        >
          <Box>
            <Typography
              variant="overline"
              sx={{
                color: t.colors.accentText,
                letterSpacing: '0.12em',
                fontSize: 10,
              }}
            >
              COMMUNITY BLUEPRINTS
            </Typography>
            <Typography
              variant="h4"
              component="h1"
              sx={{
                fontWeight: 600,
                fontSize: { xs: 28, sm: 34 },
                lineHeight: 1.2,
                mt: 0.75,
                letterSpacing: '-0.025em',
              }}
            >
              {newVersion
                ? `Publish version ${(edit?.version || 1) + 1}.`
                : blueprintId
                  ? 'Refine the details.'
                  : 'Share a sound. Start something.'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {blueprintId
                ? 'Keep your contribution useful, clear, and easy to explore.'
                : 'Turn your preset or effect chain into someone else’s next idea.'}
            </Typography>
          </Box>
        </Box>
      </FadeContent>
      {loading ? (
        <Stack spacing={2} aria-label="Loading blueprint editor">
          <Skeleton variant="rounded" height={240} />
          <Skeleton variant="rounded" height={180} />
        </Stack>
      ) : error ? (
        <Alert
          severity="error"
          action={
            <Button
              color="inherit"
              onClick={() =>
                void Promise.allSettled([
                  community.refetch(),
                  ...(blueprintId ? [detail.refetch()] : []),
                ])
              }
            >
              Retry
            </Button>
          }
        >
          {error.message}
        </Alert>
      ) : blocked ? (
        <Alert severity="warning">
          Community sharing is unavailable for your account.{' '}
          {community.data?.blueprintCommunityStatus.reason} You can view the
          decision and appeal from the Blueprints library.
        </Alert>
      ) : unavailable ? (
        <Alert severity="warning">
          This blueprint is unavailable for editing. You can only edit your own
          active blueprints.
        </Alert>
      ) : community.data ? (
        <BlueprintUploadForm
          key={`${blueprintId || 'new'}:${newVersion}`}
          edit={edit}
          newVersion={newVersion}
          tags={tags.data?.blueprintTags || []}
          creatorName={userName || 'You'}
          onClose={() => navigate(returnPath)}
          onSaved={(id, version) =>
            navigate(
              `/blueprints/${encodeURIComponent(id)}${version ? `?version=${version}` : ''}`,
              { replace: true }
            )
          }
        />
      ) : (
        <Alert severity="error">
          Could not check community sharing access. Reload this page to try
          again.
        </Alert>
      )}
    </Container>
  );
}
